import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBrandContext } from "@/contexts/brand-context";
import { useProjects } from "@/hooks/use-projects";
import {
  POST_TYPES,
  POST_TYPE_META,
  useGeneratePosts,
  type PostType,
} from "@/hooks/use-post-generation";
import { Button } from "@/components/ui/button";
import { LibraryBrowser } from "@/components/media/library-browser";
import { GenerateVideoDialog, type GenerateVideoInitial } from "@/components/media/generate-video-dialog";
import { AiVideoJobs } from "@/components/media/ai-video-jobs";
import { PlatformIcon } from "@/components/ui/platform-icon";
import { toast } from "sonner";
import { Sparkles, Check, X, Loader2 } from "lucide-react";

function groupByPlatform(types: readonly PostType[]) {
  const out: Record<string, PostType[]> = {};
  for (const t of types) {
    const platform = POST_TYPE_META[t].platform;
    if (!out[platform]) out[platform] = [];
    out[platform].push(t);
  }
  return out;
}

const TIER_LABELS: Record<1 | 2 | 3, string> = {
  1: "Single image / caption",
  2: "Multi-media",
  3: "Video",
};

export function BrandComposePage() {
  const { t } = useTranslation("compose");
  const { brand } = useBrandContext();
  const navigate = useNavigate();
  const projectsQ = useProjects(brand.id);
  const [projectId, setProjectId] = useState<string>("");
  const [brief, setBrief] = useState("");
  // Selection lives in url-space (what LibraryBrowser reports); the generation
  // call needs asset ids, derived alongside it from the selected assets.
  const [selectedUrls, setSelectedUrls] = useState<string[]>([]);
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [selectedPostTypes, setSelectedPostTypes] = useState<Set<PostType>>(new Set());
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [videoInitial, setVideoInitial] = useState<GenerateVideoInitial | null>(null);
  const openVideoDialog = (initial: GenerateVideoInitial | null = null) => {
    setVideoInitial(initial);
    setVideoDialogOpen(true);
  };

  const generate = useGeneratePosts(brand.id);

  const grouped = useMemo(() => {
    const all = POST_TYPES;
    return [1, 2, 3].map((tier) => ({
      tier: tier as 1 | 2 | 3,
      label: TIER_LABELS[tier as 1 | 2 | 3],
      byPlatform: groupByPlatform(all.filter((t) => POST_TYPE_META[t].tier === tier)),
    }));
  }, []);

  const toggle = (t: PostType) => {
    setSelectedPostTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedPostTypes(new Set(POST_TYPES));
  };
  const clearAll = () => setSelectedPostTypes(new Set());

  const handleGenerate = async () => {
    if (brief.trim().length < 5) {
      toast.error(t("toast.briefTooShort"));
      return;
    }
    if (selectedPostTypes.size === 0) {
      toast.error(t("toast.noPostType"));
      return;
    }
    try {
      const res = await generate.mutateAsync({
        projectId: projectId || undefined,
        brief: brief.trim(),
        assetIds: selectedAssetIds,
        postTypes: Array.from(selectedPostTypes),
      });
      const { generated, failed } = res;
      if (generated.length > 0) {
        const failedSuffix = failed.length ? ` (${failed.length} failed)` : "";
        toast.success(t("toast.success", { count: generated.length, failedSuffix }));
        setTimeout(() => navigate(`/${brand.slug}/review?status=PENDING_REVIEW`), 800);
      } else {
        toast.error(t("toast.allFailed", { count: failed.length, error: failed[0]?.error ?? "" }));
      }
    } catch (err: any) {
      toast.error(err?.message ?? "Generation failed");
    }
  };

  return (
    <div className="px-6 py-8 max-w-5xl mx-auto w-full space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">{t("page.title")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("page.subtitle")}
        </p>
      </header>

      <section className="rounded-lg border p-4 space-y-3">
        <div>
          <label className="text-xs font-medium uppercase text-muted-foreground">{t("fields.project")}</label>
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="mt-1 block w-full max-w-md rounded border px-3 py-2 text-sm"
          >
            <option value="">{t("fields.projectDefault")}</option>
            {(projectsQ.data ?? []).map((p: any) => (
              <option key={p.id} value={p.id}>{p.title}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs font-medium uppercase text-muted-foreground">{t("fields.brief")}</label>
          <textarea
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            placeholder={t("fields.briefPlaceholder")}
            rows={5}
            className="mt-1 block w-full rounded border px-3 py-2 text-sm"
          />
        </div>
      </section>

      <section className="rounded-lg border p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t("assets.title")}</h2>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">
              {t("assets.selected", { count: selectedAssetIds.length })}
            </span>
            <Button size="sm" variant="outline" onClick={() => openVideoDialog()}>
              <Sparkles className="h-4 w-4" />
              {t("pieces:library.aiVideo.open")}
            </Button>
          </div>
        </div>
        <AiVideoJobs brandId={brand.id} onRetry={openVideoDialog} />
        <LibraryBrowser
          brandId={brand.id}
          selectable
          multi
          selectedUrls={selectedUrls}
          onSelectChange={(urls, assets) => {
            setSelectedUrls(urls);
            setSelectedAssetIds(assets.map((a) => a.id));
          }}
        />
      </section>

      <section className="rounded-lg border p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t("generateFor.title")}</h2>
          <div className="flex gap-2 text-xs">
            <button onClick={selectAll} className="underline text-muted-foreground hover:text-foreground">{t("generateFor.selectAll")}</button>
            <span className="text-muted-foreground">·</span>
            <button onClick={clearAll} className="underline text-muted-foreground hover:text-foreground">{t("generateFor.clear")}</button>
          </div>
        </div>

        <div className="space-y-4">
          {grouped.map((g) => (
            <div key={g.tier}>
              <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                {t("generateFor.tier", { tier: g.tier, label: g.label })}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {Object.entries(g.byPlatform).map(([platform, types]) => (
                  <div key={platform} className="rounded border p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <PlatformIcon platform={platform} size={18} />
                      <span className="text-sm font-medium">{platform.replace(/_/g, " ")}</span>
                    </div>
                    <div className="space-y-1">
                      {types.map((t) => {
                        const checked = selectedPostTypes.has(t);
                        return (
                          <label key={t} className="flex items-center gap-2 text-xs cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggle(t)}
                            />
                            <span>{POST_TYPE_META[t].label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="flex justify-end">
        <Button
          onClick={handleGenerate}
          disabled={generate.isPending}
          size="lg"
          className="gap-2"
        >
          {generate.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          {generate.isPending
            ? t("actions.generating")
            : selectedPostTypes.size === 0
              ? t("actions.generateNone")
              : t("actions.generate", { count: selectedPostTypes.size })}
        </Button>
      </div>

      {generate.data && (
        <section className="rounded-lg border p-4 space-y-2">
          <h2 className="text-sm font-medium">{t("lastRun.title")}</h2>
          <ul className="space-y-1 text-xs">
            {generate.data.generated.map((g) => (
              <li key={g.pieceId} className="flex items-center gap-2 text-emerald-700">
                <Check className="h-3 w-3" /> {POST_TYPE_META[g.postType].label} ({g.postType}) — piece {g.pieceId.slice(0, 8)}
              </li>
            ))}
            {generate.data.failed.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-rose-700">
                <X className="h-3 w-3 mt-0.5" />
                <div>
                  <div>{POST_TYPE_META[f.postType].label} ({f.postType})</div>
                  <div className="text-rose-500/80">{f.error}</div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
      <GenerateVideoDialog
        brandId={brand.id}
        open={videoDialogOpen}
        onOpenChange={setVideoDialogOpen}
        initial={videoInitial}
      />
    </div>
  );
}
